import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { resolveUrl } from 'src/core/constants/api.constants';
import { PreviousCallDto } from 'src/core/models/conversation/previous-call.dto';
import { PreviousCallStatsDto } from 'src/core/models/conversation/previous-call-stats.dto';
import { CallDirection } from 'src/core/models/enum/call-direction.enum';
import { ListResponseDto } from 'src/core/models/request/list-response.dto';
import { CrudService } from 'src/core/services/crud/crud.service';
import { ConversationDto } from '../models/conversation.dto';
import { GetConversationsInput } from '../models/get-conversations-input.dto';
import { AgentInfoProfileDto } from '../models/agent-info-profile.dto';
import { AgentInfoMetricsDto } from '../models/agent-info-metrics.dto';
import { AiAgentInfoMetricsDto } from '../models/ai-agent-info-metrics.dto';

const CONVERSATION_API = ConversationDto.url;

@Injectable({ providedIn: 'root' })
export class ConversationService {
  private readonly http = inject(HttpClient);
  private readonly crudService = inject(CrudService);

  getList(input: GetConversationsInput): Observable<ListResponseDto<ConversationDto>> {
    return this.crudService.get<ConversationDto>(ConversationDto, input);
  }

  getPreviousCalls(
    contactId: number,
    page = 1,
    pageSize = 10,
    campaignId?: number | null,
  ): Observable<{ items: PreviousCallDto[]; totalCount: number }> {
    const params: Record<string, string> = {
      contactId: String(contactId),
      page: String(page),
      pageSize: String(pageSize),
    };
    if (campaignId != null) {
      params['campaignId'] = String(campaignId);
    }
    return this.http
      .get<{
        items: PreviousCallDto[];
        totalCount: number;
      }>(resolveUrl(`${CONVERSATION_API}/previous-calls`), { params })
      .pipe(
        map(res => ({
          items: (res.items || []).map(
            i =>
              ({
                ...i,
                date: i.date ? new Date(i.date) : null,
                direction:
                  typeof i.direction === 'string'
                    ? this.mapDirectionString(i.direction)
                    : i.direction,
              }) as PreviousCallDto,
          ),
          totalCount: res.totalCount ?? 0,
        })),
        catchError(error => this.handleError('getPreviousCalls', error)),
      );
  }

  getPreviousCallsStats(contactId: number): Observable<PreviousCallStatsDto> {
    return this.http
      .get<PreviousCallStatsDto>(
        resolveUrl(`${CONVERSATION_API}/previous-call-stats/${encodeURIComponent(contactId)}`),
      )
      .pipe(
        map(
          res =>
            ({
              last7Days: res.last7Days ?? 0,
              last30Days: res.last30Days ?? 0,
              total: res.total ?? 0,
              lastYear: res.lastYear ?? 0,
              lastCallAt: res.lastCallAt ?? null,
            }) as PreviousCallStatsDto,
        ),
        catchError(error => this.handleError('getPreviousCallsStats', error)),
      );
  }

  getAgentInfoProfile(agentId: string): Observable<AgentInfoProfileDto | null> {
    return this.http
      .get<AgentInfoProfileDto | null>(resolveUrl(`${CONVERSATION_API}/agent-info-profile/${encodeURIComponent(agentId)}`))
      .pipe(
        map(res => res ?? null),
        catchError(error => this.handleError('getAgentInfoProfile', error)),
      );
  }

  getAgentInfoMetrics(agentId: string): Observable<AgentInfoMetricsDto> {
    return this.http
      .get<AgentInfoMetricsDto>(resolveUrl(`${CONVERSATION_API}/agent-info-metrics/${encodeURIComponent(agentId)}`))
      .pipe(catchError(error => this.handleError('getAgentInfoMetrics', error)));
  }

  getAiAgentInfoMetrics(): Observable<AiAgentInfoMetricsDto> {
    return this.http
      .get<AiAgentInfoMetricsDto>(resolveUrl(`${CONVERSATION_API}/ai-agent-info-metrics`))
      .pipe(catchError(error => this.handleError('getAiAgentInfoMetrics', error)));
  }

  saveNote(conversationId: number, note: string): Observable<void> {
    return this.http
      .post<void>(resolveUrl(`${CONVERSATION_API}/save-conversation-note`), {
        conversationId,
        note,
      })
      .pipe(catchError(error => this.handleError('saveNote', error)));
  }

  getByConversationUid(conversationUid: string): Observable<ConversationDto | null> {
    return this.http
      .get<ConversationDto | null>(
        resolveUrl(
          `${CONVERSATION_API}/by-conversation-uid?conversationUid=${encodeURIComponent(conversationUid)}`,
        ),
      )
      .pipe(
        map(res => res ?? null),
        catchError(() => of(null)),
      );
  }

  getByAgentLegCallId(sipCallId: string): Observable<ConversationDto | null> {
    return this.http
      .get<ConversationDto | null>(
        resolveUrl(
          `${CONVERSATION_API}/by-agent-leg-call-id?sipCallId=${encodeURIComponent(sipCallId)}`,
        ),
      )
      .pipe(
        map(res => res ?? null),
        catchError(() => of(null)),
      );
  }

  getByCampaignContactId(campaignContactId: number): Observable<ConversationDto | null> {
    return this.http
      .get<ConversationDto | null>(
        resolveUrl(
          `${CONVERSATION_API}/by-campaign-contact-id?campaignContactId=${encodeURIComponent(campaignContactId)}`,
        ),
      )
      .pipe(
        map(res => res ?? null),
        catchError(() => of(null)),
      );
  }

  changeConversationContact(conversationId: number, newContactId: number): Observable<void> {
    return this.http
      .post<void>(resolveUrl(`${CONVERSATION_API}/change-conversation-contact`), {
        conversationId,
        newContactId,
      })
      .pipe(catchError(error => this.handleError('changeConversationContact', error)));
  }

  private mapDirectionString(direction: string): CallDirection {
    switch (direction) {
      case 'Outbound':
        return CallDirection.Outbound;
      case 'Internal':
        return CallDirection.Internal;
      case 'Inbound':
      default:
        return CallDirection.Inbound;
    }
  }

  private handleError(context: string, error: unknown): Observable<never> {
    console.error(`ConversationService.${context} error:`, error);
    return throwError(() => error);
  }
}
